import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/** PD-16674 — what a successful use of a payment link did. */
export type PaymentLinkUseType = 'payment' | 'payment_method';

/**
 * PD-16674 — one successful use of a payment link: the payment it took, or the
 * payment method it saved.
 *
 * This is how staff (and support) trace a payment or a saved card back to the
 * link and the email that produced it. The transaction keeps its own record of
 * the card brand and last four digits, so a card the payer chose not to save
 * still reads correctly on the payment after its saved-method row is removed;
 * `fkPaymentMethodId` then points at that removed row.
 *
 * Append-only and not audited, like `document_email_send`: the row IS the
 * record, and the transaction and payment method carry their own audit.
 */
@Index('IDX_payment_link_use_link', ['fkPaymentLinkId'])
@Entity('payment_link_use', {}, { auditable: false })
export class PaymentLinkUseEntity extends BaseEntity {
	@Column('int', { name: 'fk_payment_link__id', unsigned: true })
	public fkPaymentLinkId!: number;

	/** See {@link PaymentLinkUseType}. */
	@Column('varchar', { name: 'useType', length: 32 })
	public useType!: PaymentLinkUseType;

	/** The capture a payment produced (null for a saved payment method). */
	@Column('int', {
		name: 'fk_m_payment_transaction__id',
		unsigned: true,
		nullable: true,
	})
	public fkPaymentTransactionId!: number | null;

	/**
	 * The payment method the link vaulted: the one charged for a payment, or
	 * the one saved for a payment-method link.
	 */
	@Column('int', {
		name: 'fk_m_payment_payment_method__id',
		unsigned: true,
		nullable: true,
	})
	public fkPaymentMethodId!: number | null;

	/** What the payer asked to apply to the invoices (before any processing fee). */
	@Column('decimal', {
		name: 'amount',
		precision: 13,
		scale: 2,
		nullable: true,
	})
	public amount!: string | null;

	/**
	 * Whether the payment method stayed on the client's account afterwards:
	 * always true for a payment-method link; for a payment, whether the payer
	 * ticked "Save this payment method for future payments" (or the card was
	 * already on file).
	 */
	@Column('tinyint', { name: 'methodSaved', width: 1, default: () => "'0'" })
	public methodSaved!: boolean;
}
